// Pure helpers for the Device Master page: no network, no store, so they can be
// tested on their own.
import { collectDevices } from "../DeviceSchedule/function";
import { suggestName } from "../StoreRegions/functions";
import { COLUMNS } from "./constants";

// "  boh   ac " -> "boh ac": what a typed type is compared and stored as.
export const normalizeType = (value) => String(value ?? "").trim().replace(/\s+/g, " ");

const typeKey = (value) => normalizeType(value).toLowerCase();

// The scheduling flag as the page shows it. The service may send a boolean or
// a string; anything else reads as not set.
export const scheduleLabel = (value) => {
  if (value === true || /^(yes|true)$/i.test(String(value ?? ""))) return "Yes";
  if (value === false || /^(no|false)$/i.test(String(value ?? ""))) return "No";
  return "";
};

// One row per device across the given stores, as the service reports them.
// `houses` is the multi-house slice, keyed by store code. Stores come out in
// the order given, each store's devices by name.
export const deviceRows = (stores, houses) => {
  const rows = [];
  for (const store of stores) {
    const house = houses?.[store.houseAccessCode];
    const devices = collectDevices(house).sort((a, b) =>
      (a.name || a.deviceId).localeCompare(b.name || b.deviceId, undefined, { numeric: true })
    );
    for (const { deviceId, name, device } of devices) {
      rows.push({
        storeCode: store.houseAccessCode,
        storeName: store.houseName || store.houseAccessCode,
        deviceId,
        deviceName: name,
        // POST /v1/asset/category writes `category`; deviceType is read as a
        // fallback.
        savedType: normalizeType(device?.category ?? device?.deviceType ?? ""),
        savedSchedule: scheduleLabel(device?.scheduleConfig?.toBeScheduled ?? device?.toBeScheduled),
      });
    }
  }
  return rows;
};

// Rows with unsaved edits first, each group keeping its order. Device Master
// and Store Master both list this way, so what still has to be saved is at the
// top of the first page.
export const changedFirst = (rows) => [
  ...rows.filter((row) => row.changed),
  ...rows.filter((row) => !row.changed),
];

// How many of `fields` a row has blank.
export const unsetCount = (row, fields) => fields.filter((field) => !row[field]).length;

// Rows with the most blank `fields` first, each group keeping its order, so
// what is still to be set up is near the top of the first page. Both pages put
// unsaved rows above these (changedFirst).
export const unsetFirst = (rows, fields) =>
  rows
    .map((row, index) => ({ row, index, unset: unsetCount(row, fields) }))
    .sort((a, b) => b.unset - a.unset || a.index - b.index)
    .map(({ row }) => row);

// The fields a device is set up with: blank ones mark it "Not set".
export const DEVICE_SETUP_FIELDS = ["deviceType", "toBeScheduled"];

// Rows with the user's unsaved edits laid over what the service reported.
export const withEdits = (rows, edits = {}) =>
  rows.map((row) => {
    const edit = edits[row.deviceId] || {};
    return {
      ...row,
      deviceType: edit.deviceType ?? row.savedType,
      toBeScheduled: edit.toBeScheduled ?? row.savedSchedule,
      changed: Object.keys(edit).length > 0,
    };
  });

// Sets one field of one row's edit. A value equal to what the service reported
// drops the edit rather than keeping a no-op change.
export const setEdit = (edits, row, field, value) => {
  const saved = field === "deviceType" ? row.savedType : row.savedSchedule;
  const { [field]: dropped, ...rest } = edits[row.deviceId] || {};
  const next = value === saved ? rest : { ...rest, [field]: value };
  const { [row.deviceId]: old, ...others } = edits;
  return Object.keys(next).length ? { ...others, [row.deviceId]: next } : others;
};

// The listed spelling of a typed type (ignoring case and spacing), or null
// when it is not one of `options`.
export const existingType = (options, value) =>
  options.find((option) => typeKey(option) === typeKey(value)) ?? null;

// The request bodies a save sends, built from the edits themselves - so edits
// for devices in stores not picked right now are saved too. Only the field that
// was edited is sent: a type change goes to /v1/asset/category, a scheduling
// change to /v1/asset/scheduleConfig. A blank value cannot be sent (the
// service takes a category and a boolean), so it is left out.
export const saveRequests = (edits = {}) => {
  const categoryUpdates = [];
  const scheduleUpdates = [];
  for (const [deviceId, edit] of Object.entries(edits)) {
    if (edit.deviceType) categoryUpdates.push({ deviceId, category: edit.deviceType });
    if (edit.toBeScheduled === "Yes" || edit.toBeScheduled === "No") {
      scheduleUpdates.push({
        deviceId,
        scheduleConfig: { toBeScheduled: edit.toBeScheduled === "Yes" },
      });
    }
  }
  return { categoryUpdates, scheduleUpdates };
};

// The stores, of those given, whose devices include any of `deviceIds` - the
// ones a save changed, so only they need reading again. `houses` is the
// multi-house slice, keyed by store code.
export const storesWithDevices = (stores, houses, deviceIds) => {
  const ids = new Set(deviceIds);
  if (ids.size === 0) return [];
  return stores.filter((store) =>
    collectDevices(houses?.[store.houseAccessCode]).some(({ deviceId }) => ids.has(deviceId))
  );
};

// The edits left after `field` saved for `deviceIds`. A device with nothing
// left unsaved drops out.
export const clearSaved = (edits, field, deviceIds) => {
  const next = { ...edits };
  for (const deviceId of deviceIds) {
    if (!next[deviceId]) continue;
    const { [field]: saved, ...rest } = next[deviceId];
    if (Object.keys(rest).length) next[deviceId] = rest;
    else delete next[deviceId];
  }
  return next;
};

export const matchesSearch = (row, search) => {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  return [row.storeCode, row.storeName, row.deviceId, row.deviceName, row.deviceType]
    .some((value) => String(value ?? "").toLowerCase().includes(term));
};

// The rows as the sheet lays them out, for the CSV download.
export const csvRows = (rows) =>
  rows.map((row) => ({
    [COLUMNS.storeCode]: row.storeCode,
    [COLUMNS.storeName]: row.storeName,
    [COLUMNS.deviceId]: row.deviceId,
    [COLUMNS.deviceName]: row.deviceName,
    [COLUMNS.deviceType]: row.deviceType,
    [COLUMNS.toBeScheduled]: row.toBeScheduled,
  }));

//------------------------------------ CSV upload ------------------------------------------

// Header cells compared as bare lowercase letters, so "Device Type (Drop Down)",
// "device_type" and "DeviceType" all match. Each column takes the first header
// that starts with one of its names.
const headerKey = (value) => String(value ?? "").toLowerCase().replace(/[^a-z]/g, "");
const CSV_HEADERS = {
  storeCode: ["dpiid", "storecode", "dpi"],
  deviceId: ["deviceid"],
  deviceType: ["devicetype", "type"],
  toBeScheduled: ["tobescheduled", "scheduled", "schedule"],
};

const findColumns = (headerRow) => {
  const keys = headerRow.map(headerKey);
  const columns = {};
  for (const [field, names] of Object.entries(CSV_HEADERS)) {
    columns[field] = keys.findIndex((key) => names.some((name) => key.startsWith(name)));
  }
  return columns;
};

// The store codes a parsed CSV names, so the page can load those stores before
// building the plan. Empty when the file has no DPI ID column.
export const csvStoreCodes = (parsed) => {
  if (!parsed?.length) return [];
  const { storeCode } = findColumns(parsed[0]);
  if (storeCode === -1) return [];
  const codes = new Set();
  for (const cells of parsed.slice(1)) {
    const code = String(cells[storeCode] ?? "").trim().toUpperCase();
    if (code) codes.add(code);
  }
  return [...codes];
};

// Turns parsed CSV rows (header first) into the changes to make.
//
// `rows` are the devices that can be updated, with their current values (edits
// included). Rows are matched on deviceId and, when the file has a DPI ID, the
// device must be in that store. A blank Device Type or To be scheduled leaves
// that field as it is. Types must be one of `types` (the categories from the
// API), matched ignoring case and spacing; anything else skips the line, with
// a "did you mean" when a listed type is close. A device listed twice keeps its last line, as a
// spreadsheet edit further down would read. Every skipped line is reported with
// its line number.
export const buildCsvPlan = (parsed, { rows = [], types = [] } = {}) => {
  const errors = [];
  if (!parsed?.length) {
    return { updates: [], errors: [{ line: 0, message: "The file is empty." }], unchanged: 0 };
  }

  const columns = findColumns(parsed[0]);
  if (columns.deviceId === -1) {
    return { updates: [], errors: [{ line: 1, message: 'Header row needs a "deviceId" column.' }], unchanged: 0 };
  }
  if (columns.deviceType === -1 && columns.toBeScheduled === -1) {
    return {
      updates: [],
      errors: [{ line: 1, message: 'Header row needs a "Device Type" or "To be scheduled" column.' }],
      unchanged: 0,
    };
  }

  const byId = new Map(rows.map((row) => [String(row.deviceId).toUpperCase(), row]));
  const typeItems = types.map((name) => ({ name }));
  // deviceId -> { row, deviceType?, toBeScheduled? }
  const planned = new Map();

  parsed.slice(1).forEach((cells, index) => {
    const line = index + 2;
    const cell = (column) => (column === -1 ? "" : String(cells[column] ?? "").trim());
    const deviceId = cell(columns.deviceId);
    const storeCode = cell(columns.storeCode).toUpperCase();

    if (!deviceId) {
      errors.push({ line, message: "Missing deviceId." });
      return;
    }
    const row = byId.get(deviceId.toUpperCase());
    if (!row) {
      errors.push({
        line,
        message: storeCode
          ? `Device ${deviceId} not found in store ${storeCode}.`
          : `Device ${deviceId} is not in the selected stores.`,
      });
      return;
    }
    if (storeCode && storeCode !== String(row.storeCode).toUpperCase()) {
      errors.push({ line, message: `Device ${deviceId} is in ${row.storeCode}, not ${storeCode}.` });
      return;
    }

    const update = { row };

    const typed = normalizeType(cell(columns.deviceType));
    if (typed) {
      const type = existingType(types, typed);
      if (!type) {
        const suggestion = suggestName(typeItems, typed);
        errors.push({
          line,
          message: `"${typed}" is not a device type.${suggestion ? ` Did you mean "${suggestion}"?` : ""}`,
        });
        return;
      }
      update.deviceType = type;
    }

    const scheduleCell = cell(columns.toBeScheduled);
    if (scheduleCell) {
      const label = scheduleLabel(/^y$/i.test(scheduleCell) ? "yes" : /^n$/i.test(scheduleCell) ? "no" : scheduleCell);
      if (!label) {
        errors.push({ line, message: `"${scheduleCell}" for ${deviceId}: To be scheduled must be Yes or No.` });
        return;
      }
      update.toBeScheduled = label;
    }

    planned.set(row.deviceId, update);
  });

  // Only lines that change something count as updates.
  const updates = [];
  let unchanged = 0;
  for (const update of planned.values()) {
    const { row } = update;
    const differs =
      // A type differing only in case or spacing is the same type.
      (update.deviceType !== undefined && typeKey(update.deviceType) !== typeKey(row.deviceType)) ||
      (update.toBeScheduled !== undefined && update.toBeScheduled !== row.toBeScheduled);
    if (differs) updates.push(update);
    else unchanged += 1;
  }

  return { updates, errors, unchanged };
};

// The edits after applying a plan's updates.
export const applyCsvPlan = (edits, updates) =>
  updates.reduce((next, { row, deviceType, toBeScheduled }) => {
    let result = next;
    if (deviceType !== undefined) result = setEdit(result, row, "deviceType", deviceType);
    if (toBeScheduled !== undefined) result = setEdit(result, row, "toBeScheduled", toBeScheduled);
    return result;
  }, edits);

// The columns an upload needs: deviceId, and the two values it can set.
// DPI ID, Store Name and device Name are optional, so the sample leaves them
// out.
export const SAMPLE_COLUMNS = [COLUMNS.deviceId, COLUMNS.deviceType, COLUMNS.toBeScheduled];

// A placeholder device ID for the sample when no devices are shown. It is not
// a real device, so it has to be replaced before uploading.
export const SAMPLE_DEVICE_ID = "M000001";

// The sample file: one row per device shown on the page, with only the
// required columns. Device IDs are filled in, and each device's current type
// and scheduling too, so the file reads as "what is set now" and only the
// cells changed need editing (unchanged rows are ignored on upload).
//
// With no devices shown it is still a usable template: the headers and one
// example row, using a real device type when `types` has one.
export const sampleCsvRows = (rows = [], types = []) => {
  if (rows.length === 0) {
    return [
      {
        [COLUMNS.deviceId]: SAMPLE_DEVICE_ID,
        [COLUMNS.deviceType]: types[0] ?? "BOH AC",
        [COLUMNS.toBeScheduled]: "Yes",
      },
    ];
  }
  return rows.map((row) => ({
    [COLUMNS.deviceId]: row.deviceId,
    [COLUMNS.deviceType]: row.deviceType ?? "",
    [COLUMNS.toBeScheduled]: row.toBeScheduled ?? "",
  }));
};
